import { Clock, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createComment, deleteComment, getComments } from '@/services/reviewService';

import type { Comment } from '@/types/comment.types';

interface TimestampedNotesProps {
  applicationId: string;
  currentTime: number;
  onSeek: (time: number) => void;
}

function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function TimestampedNotes({ applicationId, currentTime, onSeek }: TimestampedNotesProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<Comment[]>([]);
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const comments = await getComments(applicationId);
        if (!cancelled) {
          const videoNotes = comments.filter((c) => c.timestampSec != null);
          videoNotes.sort((a, b) => (a.timestampSec ?? 0) - (b.timestampSec ?? 0));
          setNotes(videoNotes);
        }
      } catch {
        // Silently fail — notes are non-critical
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  const handleAdd = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isSaving) return;

    setIsSaving(true);
    try {
      const comment = await createComment({
        applicationId,
        text: trimmed,
        timestampSec: Math.floor(currentTime),
      });
      setNotes((prev) => [...prev, comment].sort((a, b) => (a.timestampSec ?? 0) - (b.timestampSec ?? 0)));
      setText('');
    } catch {
      // Could show toast, but keeping simple
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, currentTime, text, isSaving]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleAdd();
      }
    },
    [handleAdd],
  );

  const handleRemove = useCallback(async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setNotes((prev) => prev.filter((n) => n.commentId !== commentId));
    } catch {
      // Silently fail
    }
  }, []);

  if (isLoading) {
    return (
      <div className='bg-white shadow rounded-lg p-6 flex items-center justify-center'>
        <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
      </div>
    );
  }

  return (
    <div className='bg-white shadow rounded-lg overflow-hidden'>
      <div className='px-4 py-3 bg-gray-50 border-b border-gray-200'>
        <h4 className='text-sm font-semibold text-gray-700 uppercase tracking-wide'>
          {t('submissionDetail.video.timestampedNotes')}
        </h4>
      </div>

      <div className='p-4 space-y-3'>
        {/* Add note input */}
        <div className='flex items-start gap-2'>
          <button
            type='button'
            className='shrink-0 mt-1.5 flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-mono text-gray-600'
            onClick={() => onSeek(currentTime)}
            title={t('submissionDetail.video.currentTimestamp')}>
            <Clock className='h-3 w-3' />
            {formatTimestamp(currentTime)}
          </button>
          <input
            type='text'
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('submissionDetail.video.notePlaceholder')}
            disabled={isSaving}
            className='flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50'
          />
          <button
            type='button'
            onClick={() => void handleAdd()}
            disabled={!text.trim() || isSaving}
            className='shrink-0 rounded-md bg-blue-600 p-1.5 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'>
            {isSaving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Plus className='h-4 w-4' />}
          </button>
        </div>

        {/* Notes list */}
        {notes.length === 0 ? (
          <p className='text-xs text-gray-400 text-center py-2'>{t('submissionDetail.video.noNotes')}</p>
        ) : (
          <ul className='space-y-2'>
            {notes.map((note) => (
              <li key={note.commentId} className='flex items-start gap-2 group'>
                <button
                  type='button'
                  onClick={() => onSeek(note.timestampSec ?? 0)}
                  className='shrink-0 mt-0.5 rounded bg-blue-50 px-2 py-0.5 text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors'>
                  {formatTimestamp(note.timestampSec ?? 0)}
                </button>
                <span className='flex-1 text-sm text-gray-700'>{note.text}</span>
                <button
                  type='button'
                  onClick={() => void handleRemove(note.commentId)}
                  className='shrink-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all'>
                  <Trash2 className='h-3.5 w-3.5' />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
